import { createContext, useContext, useState, useEffect } from "react";

function useSessionState(key, defaultValue) {
    const [state, setStateInternal] = useState(() => {
        try {
            const storedValue = sessionStorage.getItem(key);
            if (storedValue !== null) {
                const parsed = JSON.parse(storedValue);
                const isDate = typeof parsed === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(parsed);
                if (isDate) {
                    return new Date(parsed);
                }
                return parsed;
            }
        } catch (e) {
            console.error("Error reading sessionStorage", e);
        }
        return defaultValue;
    });

    const setState = (newValue) => {
        setStateInternal((prev) => {
            const value = typeof newValue === 'function' ? newValue(prev) : newValue;
            try {
                if (value !== undefined) {
                    sessionStorage.setItem(key, JSON.stringify(value));
                }
            } catch (e) {
                console.warn("Storage quota exceeded or error writing to sessionStorage", e);
            }
            return value;
        });
    };

    useEffect(() => {
        try {
            if (state !== undefined) {
                sessionStorage.setItem(key, JSON.stringify(state));
            }
        } catch (e) {
            console.warn("Storage quota exceeded or error writing to sessionStorage", e);
        }
    }, [key, state]);

    return [state, setState];
}

export const TicketFilterContext = createContext();

export const TicketFilterProvider = ({ children }) => {
    const [tripType, setTripType] = useSessionState("tf_tripType", "One Way");
    const [adultsCount, setAdultsCount] = useSessionState("tf_adultsCount", 1);
    const [childrenCount, setChildrenCount] = useSessionState("tf_childrenCount", 0);
    const [infantsCount, setInfantsCount] = useSessionState("tf_infantsCount", 0);
    const [flightTickets, setFlightTickets] = useState([]); // Don't persist large flight list
    const [departureCity, setDepartureCity] = useSessionState("tf_departureCity", null);
    const [arrivalCity, setArrivalCity] = useSessionState("tf_arrivalCity", null);
    const [departureDate, setDepartureDate] = useSessionState("tf_departureDate", new Date());
    const [returnDate, setReturnDate] = useSessionState("tf_returnDate", new Date());
    const [flightStops, setFlightStops] = useSessionState("tf_flightStops", false);
    const [flightPriceRange, setFlightPriceRange] = useSessionState("tf_flightPriceRange", null);
    const [ticketCount, setTicketCount] = useSessionState("tf_ticketCount", null);
    const [ticketClass, setTicketClass] = useSessionState("tf_ticketClass", null);
    const [airLinePreference, setAirLinePreference] = useSessionState("tf_airLinePreference", null);
    const [currencyPreference, setCurrencyPreference] = useSessionState("tf_currencyPreference", "PKR");
    const [multicityFlights, setMulticityFlights] = useSessionState("tf_multicityFlights", [
        {
            from: { code: 'LHE', name: 'Lahore' },
            to: { code: 'JED', name: 'King Abdulaziz International' },
            depart: new Date(),
        },
        {
            from: { code: 'JED', name: 'King Abdulaziz International' },
            to: { code: '', name: 'Select a City' },
            depart: new Date(),
        },
    ]);
    const [selectedArCode, setSelectedArCode] = useSessionState("tf_selectedArCode", null);
    const [markupPreference, setMarkupPreference] = useSessionState("tf_markupPreference", 'Amount');
    const [markupvalue, setMarkupValue] = useSessionState("tf_markupvalue", '');
    const [directFlight, setDirectFlight] = useSessionState("tf_directFlight", false);
    const [nearByAirport, setNearByAirport] = useSessionState("tf_nearByAirport", true);
    const [onwardClass, setOnwardClass] = useSessionState("tf_onwardClass", 'Economy');
    const [returnClass, setReturnClass] = useSessionState("tf_returnClass", 'Economy');
    const [gdsSelection, setGdsSelection] = useSessionState("tf_gdsSelection", 'Both');
    const [totalAmount, setTotalAmount] = useState(0);

    const [departDate, setDepartDate] = useSessionState("tf_departDate", new Date());
    const [toLocation, setToLocation] = useSessionState("tf_toLocation", {
        code: 'JED',
        name: 'King Abdulaziz International',
        flag: 'KSA'
    });
    const [fromLocation, setFromLocation] = useSessionState("tf_fromLocation", {
        code: 'LHE',
        name: 'Lahore',
        flag: 'PK'
    });
    const [isAlreadyAdded, setIsAlreadyAdded] = useSessionState("tf_isAlreadyAdded", false);
    const [selectedAirlines, setSelectedAirlines] = useSessionState("tf_selectedAirlines", []);


    function resetFilters() {
        setFlightStops(false);
        setFlightPriceRange(null);
        setTicketCount(null)
        setTicketClass(null);
        setAirLinePreference(null)
        setCurrencyPreference("PKR")
        setSelectedArCode(null);
        setMarkupPreference('Amount');
        setMarkupValue('');
        // setOnwardClass("Economy");
        // setReturnClass("Economy");
        // setDirectFlight(false);
        // setNearByAirport(true);
    }

    function resetFiltersState() {
        setTripType("One Way");
        setAdultsCount(1);
        setChildrenCount(0);
        setInfantsCount(0);
        setFlightTickets([]);
        setDepartureCity(null);
        setArrivalCity(null);
        setDepartureDate(new Date());
        setDepartDate(new Date());
        setReturnDate(new Date());
        setFlightStops(false);
        setFlightPriceRange(null);
        setTicketCount(null);
        setTicketClass(null);
        setAirLinePreference(null);
        setCurrencyPreference("PKR");
        // setMulticityFlights([{ departureCity: null, arrivalCity: null, departureDate: null }]);
        setSelectedArCode(null);
        setMarkupPreference('Amount');
        setMarkupValue('');
        setDirectFlight(false);
        setNearByAirport(true);
        // setOnwardClass("Economy");
        // setReturnClass("Economy");
    }

    return (
        <TicketFilterContext.Provider
            value={{
                tripType, setTripType,
                adultsCount, setAdultsCount,
                childrenCount, setChildrenCount,
                infantsCount, setInfantsCount,
                flightTickets, setFlightTickets,
                departureCity, setDepartureCity,
                arrivalCity, setArrivalCity,
                departureDate, setDepartureDate,
                returnDate, setReturnDate,
                flightStops, setFlightStops,
                flightPriceRange, setFlightPriceRange,
                ticketCount, setTicketCount,
                ticketClass, setTicketClass,
                airLinePreference, setAirLinePreference,
                currencyPreference, setCurrencyPreference,
                multicityFlights, setMulticityFlights,
                selectedArCode, setSelectedArCode,
                markupPreference, setMarkupPreference,
                markupvalue, setMarkupValue,
                directFlight, setDirectFlight,
                nearByAirport, setNearByAirport,
                onwardClass, setOnwardClass,
                returnClass, setReturnClass,
                toLocation, setToLocation,
                fromLocation, setFromLocation,
                departDate, setDepartDate,
                isAlreadyAdded, setIsAlreadyAdded,
                gdsSelection, setGdsSelection,
                selectedAirlines, setSelectedAirlines,
                totalAmount, setTotalAmount,

                resetFilters, resetFiltersState
            }}
        >
            {children}
        </TicketFilterContext.Provider>
    );
};

export function useTicketFilterValues() {
    return useContext(TicketFilterContext);
}